import React from "react";
import { useState, useEffect, useCallback, useRef, useContext } from "react";
import { useFetch } from "use-http";
import { GoogleMapsConfig } from "./const/GoogleMapsConfig";
import { Constants } from "./const/Constants";

import { LocStrings } from "./const/LocStrings";

import "./assets/css/style-wellmax.css";
import GoogleMapReact from "google-map-react";
import CenterMapMarker from "./components/CenterMapMarker";
import GeolocationProbe from "./components/GeolocationProbe";
import UserLocationMarker from "./components/UserLocationMarker";
import CenterInfoCell from "./components/CenterInfoCell";
import { haversine_distance } from "./helpers/Helpers";

import "./CentersLocator.css";

function CentersLocator(props) {
  const mapRef = useRef();
  const locatorCentersListRef = useRef();

  const apiPoint =
    document.location.hostname.indexOf("localhost") != -1 || document.location.hostname.indexOf(".test") != -1
      ? "http://easyscripts.test"
      : "//" + document.location.hostname;

  const { get, post, response, loading, error } = useFetch(apiPoint); //"http://healthsun-wmp.test")
  const [centers, setCenters] = useState([]);

  const [activeCenterID, setActiveCenterID] = useState(0);

  const [userLocation, setUserLocation] = useState(null); //user location is either received from browser geoloc, or by user searching by keyword
  const [searchLocationName, setSearchLocationName] = useState("");
  const [geolocatorRequested, setGeolocatorRequested] = useState(false);
  const [geolocationResult, setGeolocationResult] = useState(null); //success or error
  const [geocoderObject, setGeocoderObject] = useState(null);
  const [currentLanguage, setCurrentLanguage] = useState("");

  const [googleMap, setGoogleMap] = useState(null);
  const [googleMapsObject, setGoogleMapsObject] = useState(null);
  const [mapZoom, setMapZoom] = useState(10);
  const [mapCenter, setMapCenter] = useState({
    lat: Constants.mapCenterLat,
    lng: Constants.mapCenterLng,
  });

  const [mobileViewMode, setMobileViewMode] = useState("list"); //list or map
  const [isMobileMode, setIsMobileMode] = useState(false);

  const [selectedCenterID, setSelectedCenterID] = useState(null);

  const isIE11 = !!window.MSInputMethodContext && !!document.documentMode;

  const createMapOptions = (maps) => {
    return {
      zoomControlOptions: {
        style: maps.ZoomControlStyle.SMALL,
      },
      mapTypeControl: false,
      fullscreenControl: false,
      clickableIcons: false,
    };
  };

  const loadInitialCenters = useCallback(async () => {
    var curLang = "en";
    if (document.location.pathname.indexOf("/es") !== -1) {
      curLang = "es";
    } else {
      curLang = "en";
    }

    const centersList = await get("/wp-json/pharmacies/v1/list?lang=" + curLang);
    if (response.ok) {
      console.log("Got pharms listing:", centersList);
      setCenters([...centersList]);
    } else {
      console.error("Error getting pharmacies list");
    }
  }, [get, response]);

  const handleLocationNameChange = (event) => {
    const newValue = event.target.value;
    setSearchLocationName(newValue);
    // if (newValue.length>3) {
    //
    // }
  };

  useEffect(() => {
    if (userLocation != null) {
      updatedCentersDistances();
    }
  }, [userLocation]);

  const scrollCurrentCenterIntoView = () => {
    //both the list panel and the map
    console.log("Scrolling stuff into view - trying");
    if (!googleMapsObject || !googleMap) return;

    //we check if the active center's marker is visible on the map
    const activeCenter = centers.find((center) => center.id === activeCenterID);
    if (activeCenter) {
      const centerPos = new googleMapsObject.LatLng(activeCenter.location.latitude, activeCenter.location.longitude);

      if (!googleMap.getBounds().contains(centerPos)) {
        console.log("Need to adjust map bounds");
        fitMarkersToBounds();
      }
    }

    //we also checking if the map cell is visible in the list
  };

  useEffect(() => {
    scrollCurrentCenterIntoView();
  }, [activeCenterID]);

  useEffect(() => {
    //page load
    if (document.location.pathname.indexOf("/es") !== -1) {
      LocStrings.setLanguage("es");
      setCurrentLanguage("es");
    } else {
      LocStrings.setLanguage("en");
      setCurrentLanguage("en");
    }
  }, []);

  const updatedCentersDistances = () => {
    if (userLocation == null) {
      console.log("NOT distancing centers");
      //return centers
    } else {
      console.log("DIST: Updating centers distances");
      let centersWithDistance = centers.map((center) => {
        const userLoc = {
          lat: userLocation.lat,
          lng: userLocation.lng,
        };
        const center1loc = {
          lat: center.location.latitude,
          lng: center.location.longitude,
        };
        const center1dist = haversine_distance(userLoc, center1loc);

        const newCent = {
          ...center,
          distance: center1dist,
        };
        console.log("DIST:", newCent);
        return newCent;
      });

      console.log("DIST: Centers WITH distances:", centersWithDistance);

      centersWithDistance.sort((a, b) => (a.distance > b.distance ? 1 : -1));

      console.log("DIST: Centers WITH distances:", centersWithDistance);

      setCenters(centersWithDistance);
    }
  };

  const fitMarkersToBounds = useCallback(() => {
    //first we need to figure out the map bounds
    console.log("Try fitting?");

    if (!googleMapsObject || !googleMap) return;

    if (centers.length === 0) return;

    console.log("Map ref is:", mapRef.current.clientWidth);

    var boundsPadding = {
      top: 90,
      bottom: 20,
      left: mapRef.current.clientWidth / 2,
      right: 60,
    };
    if (isMobileMode) {
      boundsPadding = {
        top: 90,
        bottom: 20,
        left: 60,
        right: 60,
      };
    }

    let bounds = new googleMapsObject.LatLngBounds();
    centers.map((center) => {
      let pos = new googleMapsObject.LatLng(center.location.latitude, center.location.longitude);
      bounds.extend(pos);
      return true;
    });

    if (userLocation !== null) {
      console.log("Extending bounds based on user location");
      let pos = new googleMapsObject.LatLng(userLocation.lat, userLocation.lng);
      bounds.extend(pos);
    }

    googleMap.fitBounds(bounds, boundsPadding);

    if (googleMap.getZoom() > Constants.maxMapZoom) {
      googleMap.setZoom(Constants.maxMapZoom);
    }
  }, [centers, userLocation, googleMapsObject, googleMap, mobileViewMode]);

  const adjustMapHolder = () => {
    if (centers === undefined || centers.length == 0) {
      return;
    }
    // adjustMapHolderHeight();
    fitMarkersToBounds();
  };

  const handleMapChange = (arg) => {
    // console.log("MAP CHANGE EVENT:", arg)
    // fitMarkersToBounds();
  };

  const onGoogleAPILoaded = ({ map, maps }) => {
    console.log("GOOGLE API LOADED:", map, maps);
    setGoogleMap(map);
    setGoogleMapsObject(maps);
    adjustMapHolder();
  };

  const locateUserByKeyword = (keyword) => {
    if (geocoderObject === null) return;
    if (searchLocationName.length < 4) return;
    console.log("Loading user by keyword:", keyword);

    geocoderObject.geocode(
      {
        address: keyword,
        region: "US",
      },
      (results, status) => {
        if (status == googleMapsObject.GeocoderStatus.OK) {
          console.log("****Geocoder results:", results);
          setUserLocation({
            lat: results[0].geometry.location.lat(),
            lng: results[0].geometry.location.lng(),
            accuracy: null,
          });
          console.log("Set user coordinates to:", userLocation);
          setGeolocationResult("manual_success");

          if (centers.length > 0) {
            const centerloc = {
              lat: centers[0].location.latitude,
              lng: centers[0].location.longitude,
            };
            const userLoc = {
              lat: results[0].geometry.location.lat(),
              lng: results[0].geometry.location.lng(),
            };
            const userDistance = haversine_distance(userLoc, centerloc);
            if (userDistance > Constants.userDistanceKilometersThreshold) {
              setGeolocationResult("too_far");
              setUserLocation(null);
            }
          }
        } else {
          console.log("Got error geocoding coordinates:", keyword);
          console.error(status);
          setGeolocationResult("manual_error");
        }
      }
    );
  };

  useEffect(() => {
    console.warn("Google map initialized");
    if (googleMap && googleMapsObject) {
      console.log("Setting geocoder object");
      setGeocoderObject(new googleMapsObject.Geocoder());
    }
    console.log("Using api key:", GoogleMapsConfig.apiKey);
  }, [googleMapsObject, googleMap]);

  useEffect(() => {
    loadInitialCenters();
  }, [loadInitialCenters]);

  useEffect(() => {
    //updating markers location
    fitMarkersToBounds();
  }, [centers, fitMarkersToBounds]);

  // const [scrollTop, setScrollTop] = useState(0);
  // useEffect(() => {
  // 		function onScroll() {
  // 				let currentPosition = window.pageYOffset; // or use document.documentElement.scrollTop;
  // 				setScrollTop(currentPosition <= 0 ? 0 : currentPosition);
  // 		}
  //
  // 		window.addEventListener("scroll", onScroll);
  // 		return () => window.removeEventListener("scroll", onScroll);
  // }, []);

  useEffect(() => {
    console.log("Setting is mobile:", window.innerWidth < 767);
    setIsMobileMode(window.innerWidth < 767);
  }, []);

  useEffect(() => {
    function onResize() {
      adjustMapHolder();
      setIsMobileMode(window.innerWidth < 767);
    }

    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, [adjustMapHolder]);

  const updateUserLocationFromProbe = (geoloc) => {
    console.log("Updating geoloc inside callback:", geoloc);
    setUserLocation({
      ...userLocation,
      lat: geoloc.latitude,
      lng: geoloc.longitude,
      accuracy: geoloc.accuracy,
    });
    console.log("New user location will be:", geoloc);
  };

  useEffect(() => {
    if (userLocation != null && geolocationResult == "success" && centers.length > 0) {
      //checking actual distance
      const centerloc = {
        lat: centers[0].location.latitude,
        lng: centers[0].location.longitude,
      };
      const userLoc = {
        lat: userLocation.lat,
        lng: userLocation.lng,
      };
      const userDistance = haversine_distance(userLoc, centerloc);
      if (userDistance > Constants.userDistanceKilometersThreshold) {
        setGeolocationResult("too_far");
        setUserLocation(null);
      }
    }
  }, [userLocation, geolocationResult, centers]);

  const getLocationString = (userLoc) => {
    if (!!userLoc.accuracy) {
      if (userLoc.accuracy < 1000) {
        return LocStrings.accurateLocationDetected;
      } else {
        return LocStrings.innacurateLocationDetected;
      }
    } else {
      return LocStrings.locationDetected;
    }
  };

  return (
    <div className="c-locator">
      {isMobileMode === true && (
        <div className="c-locator__mobile-switch" role="tablist" aria-label="Switch view">
          <button
            aria-selected={mobileViewMode === "list"}
            role="tab"
            className="c-locator__tab"
            id="list-view-tab"
            onClick={() => setMobileViewMode("list")}
          >
            {LocStrings.listView}
          </button>
          <button
            aria-selected={mobileViewMode === "map"}
            role="tab"
            className="c-locator__tab"
            id="map-view-tab"
            onClick={() => setMobileViewMode("map")}
          >
            {LocStrings.mapView}
          </button>
        </div>
      )}

      <div role="tabpanel" id="map-view-panel" aria-labelledby="map-view-tab" className="c-locator__map" ref={mapRef}>
        <GoogleMapReact
          onGoogleApiLoaded={onGoogleAPILoaded}
          yesIWantToUseGoogleMapApiInternals
          bootstrapURLKeys={{ key: GoogleMapsConfig.apiKey }}
          resetBoundsOnResize={false}
          center={mapCenter}
          zoom={mapZoom}
          maxZoom={5}
          onChange={handleMapChange}
          options={createMapOptions}
        >
          {centers.map((center, idx) => {
            if (center.should_show_address && center.location.latitude && center.location.longitude) {
              return (
                <CenterMapMarker
                  key={center.id}
                  idx={idx}
                  centerData={center}
                  lat={center.location.latitude}
                  lng={center.location.longitude}
                  activeCenterID={activeCenterID}
                  setActiveCenterID={setActiveCenterID}
                />
              );
            } else {
              console.warn("Center doesn't have location defined:", center);
              return null;
            }
          })}

          {userLocation && <UserLocationMarker lat={userLocation.lat} lng={userLocation.lng} />}
        </GoogleMapReact>
      </div>

      {(mobileViewMode === "list" || !isMobileMode) && (
        <div role="tabpanel" id="list-view-panel" aria-labelledby="list-view-tab" className="c-locator__pane">
          <div className="c-locator__form is-loading">
            <h2>{LocStrings.findPharmacyNearYou}</h2>
            <div className="c-form">
              <input
                className="c-locator__form-search"
                type="text"
                placeholder={LocStrings.yourLocation}
                onChange={handleLocationNameChange}
                value={searchLocationName}
              />
              <div className="c-locator__form-controls">
                <button
                  disabled={searchLocationName.length < 3}
                  className="ui-btn ui-btn--submit is-loading"
                  type="button"
                  value="Submit"
                  onClick={() => {
                    locateUserByKeyword(searchLocationName);
                  }}
                >
                  {LocStrings.submit}
                </button>
                <div>{LocStrings.or}</div>
                <button
                  className="ui-btn ui-btn--outline ui-btn--outline-black ui-btn--locate"
                  type="button"
                  onClick={() => {
                    console.log("Geolocator Requested");
                    setSearchLocationName("");
                    setGeolocatorRequested(true);
                  }}
                >
                  {LocStrings.locateMe}
                </button>
              </div>

              {geolocatorRequested && (
                <GeolocationProbe
                  setGeolocationCallback={updateUserLocationFromProbe}
                  setGeolocationResult={setGeolocationResult}
                />
              )}
              {geolocatorRequested && userLocation === null && geolocationResult === null && (
                <div className="c-locator-notes">{LocStrings.locatingPleaseWait}</div>
              )}
              {geolocationResult === "error" && (
                <div className="c-locator-notes error">{LocStrings.locationUnavailable}</div>
              )}
              {geolocationResult === "success" && (
                <div className="c-locator-notes success">{getLocationString(userLocation)}</div>
              )}
              {geolocationResult === "too_far" && (
                <div className="c-locator-notes error">{LocStrings.locationIsTooFar}</div>
              )}
              {geolocationResult === "manual_error" && (
                <div className="c-locator-notes error">{LocStrings.thisLocationCouldntBeFound}</div>
              )}
            </div>
          </div>
          {centers && centers.length > 0 && (
            <div className="c-locator__centers" ref={locatorCentersListRef}>
              {centers.length > 0 &&
                centers.map((center, idx) => {
                  return (
                    <CenterInfoCell
                      parentHolderRef={locatorCentersListRef}
                      key={center.id}
                      idx={idx}
                      centerData={center}
                      userLocation={userLocation}
                      activeCenterID={activeCenterID}
                      setActiveCenterID={setActiveCenterID}
                    />
                  );
                })}
            </div>
          )}
        </div>
      )}
      {loading && (
        <div className="c-locator__loading">
          <div className="visually-hidden">{LocStrings.loadingYourResults}</div>
          <i></i>
        </div>
      )}
    </div>
  );
}

export default CentersLocator;
